import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { env } from '@/app/config/env';
import { StatusPage } from '@/shared/components/errors/status-page';

/**
 * Last-resort boundary around the whole tree.
 *
 * React Router's `errorElement` catches errors thrown inside routes; this
 * catches everything outside them — a provider failing, or a render crash in
 * the shell itself. Without it those produce a blank white page.
 *
 * Still a class component: error boundaries have no hook equivalent.
 */
interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Replace with the real error reporter when one is adopted. Deliberately
    // `console.error`, not `console.log`: this is a genuine failure, and the
    // lint rule permits it for exactly this reason.
    console.error('Unhandled application error:', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <StatusPage
        icon={AlertOctagon}
        code="Error"
        title="The application stopped unexpectedly"
        description={
          env.isProduction
            ? 'Something went wrong that we could not recover from. Reloading should restore the app.'
            : this.state.error.message
        }
        actions={
          <Button size="sm" onClick={() => window.location.reload()}>
            Reload application
          </Button>
        }
      />
    );
  }
}
